import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row, Spin } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import pluralize from 'pluralize';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useDidUpdate } from 'rooks';

import { useAppDispatch } from 'hooks/useAppDispatch';
import AssignmentCard from '../AssignmentCard';
import { getPartnerAssignments } from 'redux/reducers/assignmentSlice';
import {
  UserPagedAssignmentResponseModel,
  AssignmentTitleInfoModel,
} from 'api/backofficeApiClient';
import BaseSearch from 'components/wrappers/BaseSearch';
import { AssigmentQueryType } from '../AssignmentsSection/AssignmentsSection';

export default function PartnerAssignmentsSection() {
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const [partnerAssignments, setPartnerAssignments] = useState<
    AssignmentTitleInfoModel[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { query, section } = queryString.parse(location.search);

  useEffect(() => {
    initInfinityScroll();
  }, [query]);

  useDidUpdate(() => {
    const queryData: AssigmentQueryType = {
      query,
      section,
    };

    navigate(`/assignments?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setPartnerAssignments([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetTemplates = useCallback(
    (result: PayloadAction<UserPagedAssignmentResponseModel>) => {
      const loadedPartnerAssignments = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newPartnerAssignments = [
        ...partnerAssignments,
        ...loadedPartnerAssignments,
      ];
      const hasMoreRows = newPartnerAssignments.length < totalCount;

      setHasMore(hasMoreRows);
      setPartnerAssignments(newPartnerAssignments);
      setTotalCount(totalCount);
    },
    [partnerAssignments],
  );

  const loadPartnerAssignments = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getPartnerAssignments({
          requestId,
          pageNumber,
          searchQuery: query ? query.trim() : undefined,
        }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getPartnerAssignments.fulfilled.match(result)) {
          onSetTemplates(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetTemplates, query],
  );

  const getTotalCountMessage = () =>
    pluralize('partner assignment', totalCount, true);

  const getNoPartnerAssignmentsMessage = () =>
    'You have no partner assignments.';

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by assignment name, ID"
              />
            </Col>

            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountMessage()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      {getNoPartnerAssignmentsMessage()}
                    </span>
                  )}
                </>
              )}

              {isVisible && (
                <InfiniteScroll
                  pageStart={-1}
                  loadMore={loadPartnerAssignments}
                  hasMore={hasMore}
                  loader={<Spin key={0} />}
                  useWindow={false}
                  getScrollParent={() => infiniteScrollParentRef.current}
                >
                  <Row gutter={[20, 20]}>
                    {partnerAssignments.map(
                      (assignment: AssignmentTitleInfoModel) => (
                        <Col
                          key={assignment.assignmentId}
                          xs={24}
                          md={12}
                          className="d-flex"
                        >
                          <AssignmentCard
                            assignment={assignment}
                            isPartner={true}
                          />
                        </Col>
                      ),
                    )}
                  </Row>
                </InfiniteScroll>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
